import SectionHeading from '@/pages/home/components/SectionHeading';
import Reveal from '@/components/base/Reveal';
import ContactForm from '@/pages/home/components/ContactForm';
import { aboutPrinciples, siteContact, testimonials } from '@/mocks/home';

export default function AboutSection() {
  return (
    <section id="about" className="scroll-mt-24 bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <SectionHeading
              index="08"
              kicker="ABOUT US"
              title="关于 51.吃瓜：把热闹还原成一条清晰的时间线"
              desc="我们做的是一个「事件补课」站，服务的是那些对热点感兴趣、但不想被碎片信息牵着走的人。与其抢第一时间的快讯，不如把一件事的来龙去脉整理到可以被反复回看。"
            />
            <div className="mt-6 space-y-4 font-body text-sm leading-relaxed text-foreground-600">
              <p>
                网站的定位很简单：当你搜索一件事的<strong className="font-semibold text-foreground-900">前情回顾</strong>或
                <strong className="font-semibold text-foreground-900">事件全貌</strong>时，我们希望给出的不是又一条传闻，
                而是一份能自己核对的结构化梳理。所有条目按时间排列，争议双方的说法并排呈现，
                读者只需要看一眼时间线，就能知道哪句话是新的、哪句话已经过期。
              </p>
              <p>
                我们也把这件事当成一种习惯：每次跟进都标注日期，每次更正都留下痕迹。
                热闹会过去，但一条经得起复盘的记录，会长久有用。
              </p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-background-200 sm:col-span-2">
                <div className="h-48 w-full md:h-56">
                  <img
                    src="https://readdy.ai/api/search-image?query=top%20down%20editorial%20flat%20lay%20of%20watermelon%20slices%20on%20a%20warm%20cream%20table%20with%20a%20notebook%20and%20pencil%2C%20soft%20natural%20light%2C%20muted%20red%20and%20olive%20green%20palette%2C%20calm%20documenting%20mood&width=1200&height=700&seq=chigua-about-2026&orientation=landscape"
                    alt="51.吃瓜 内容整理与事件补课示意"
                    title="51.吃瓜 - 事件补课与内容原则"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              {aboutPrinciples.map((p, i) => (
                <Reveal key={p.title} delay={i * 70}>
                  <div className="h-full rounded-lg border border-background-200 bg-background-100/60 p-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${p.icon} text-lg`} />
                    </span>
                    <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 font-body text-xs leading-relaxed text-foreground-600">
                      {p.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-primary-300" />
            <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              吃过瓜的人怎么说
            </h3>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 70}>
                <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100/50 p-5">
                  <span className="w-6 h-6 flex items-center justify-center text-primary-300">
                    <i className="ri-double-quotes-l text-xl" />
                  </span>
                  <blockquote className="mt-3 flex-1 font-body text-xs leading-relaxed text-foreground-700 md:text-sm">
                    {t.text}
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full">
                      <img
                        src={t.avatar}
                        alt={`${t.name} 的头像`}
                        title={`${t.name} - 51.吃瓜 用户评价`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="flex flex-col leading-tight">
                      <span className="font-heading text-sm font-bold text-foreground-950">
                        {t.name}
                      </span>
                      <span className="font-label text-[11px] text-foreground-500">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100/60 p-5 md:mt-20 md:p-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-primary-300" />
              <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                联系我们
              </h3>
            </div>
            <p className="mt-4 font-body text-sm leading-relaxed text-foreground-600">
              有完整的公开线索，或者发现站内某条信息需要更正？直接告诉我们。我们更看重有出处、有时间的信息。
            </p>
            <a
              href={`mailto:${siteContact.email}`}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line" />
              </span>
              {siteContact.email}
            </a>
            <p className="mt-4 font-body text-xs leading-relaxed text-foreground-500">
              {siteContact.slogan} 本页内容为示例演示，人物与情节均经过处理。
            </p>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}